// src/components/partial-pages/VoicePage/VoiceDevicesModal/VoiceDevicesModal.tsx

import React, { memo, useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@material-ui/core'
import { BRAND_COLOR, ALL_ORGS_SELECTED } from 'lib/constants'
import type { VoiceGroupViewModel } from '../VoiceCards'
import { useChannels } from 'contexts/ChannelsProvider'
import { useGlobal } from 'contexts/GlobalProvider'
import {
  setAlertSourceDeviceGroup,
  addAlertSourceToDeviceGroup,
  removeAlertSourceFromDeviceGroup,
} from 'models/api/deviceGroups'
import { getRequestV5, ApiResponseV5 } from 'models/api/rest'

import type { Device } from './types'
import { DevicePickerModal } from './DevicePickerModal'
import {
  deviceGroupChannelLanguageLabel,
  normalizeDeviceId,
  normalizeDeviceName,
  uniqById,
  languageLabel,
} from './helpers'
import { ConditionalRender } from 'components/widgets'
import { isAdmin } from 'lib/utils/auth'

type Props = {
  open: boolean
  group: VoiceGroupViewModel | null
  onClose: () => void
}

function VoiceDevicesModal({ open, group, onClose }: Props) {
  const { selectedOrgId } = useGlobal()
  const { deviceGroups, refreshDeviceGroups } = useChannels()

  const [pickerOpen, setPickerOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  const [voiceButtons, setVoiceButtons] = useState<any[]>([])
  const [loadingVoiceButtons, setLoadingVoiceButtons] = useState(false)
  const [voiceButtonsMeta, setVoiceButtonsMeta] = useState<any>({ currentPage: 1 })

  const dg = useMemo(() => {
    if (!group) return null
    const gid = String(group.id || '')
    if (!gid) return null
    return (deviceGroups || []).find(x => String((x as any)?.id) === gid) || null
  }, [deviceGroups, group])

  const thisGroupLabel = useMemo(() => {
    if (!dg) return group?.linkedChannelName || group?.baseChannelName || ''
    return deviceGroupChannelLanguageLabel(dg)
  }, [dg, group])

  const assignmentByDeviceId = useMemo(() => {
    const map = new Map<string, { groupId: string; label: string }>()
    if (!selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED) return map

    const multi = new Set<string>()

    for (const g of deviceGroups || []) {
      if (String((g as any)?.orgId || '') !== String(selectedOrgId)) continue
      const groupId = String((g as any)?.id ?? '')
      const label = deviceGroupChannelLanguageLabel(g)

      const list = Array.isArray((g as any)?.alertSources) ? (g as any).alertSources : []
      for (const src of list) {
        const id = normalizeDeviceId(src)
        if (!id) continue

        if (!map.has(id)) map.set(id, { groupId, label })
        else if (map.get(id)?.groupId !== groupId) multi.add(id)
      }
    }

    multi.forEach(id => {
      const prev = map.get(id)
      if (prev) map.set(id, { groupId: prev.groupId, label: 'Multiple' })
      else map.set(id, { groupId: '', label: 'Multiple' })
    })

    return map
  }, [deviceGroups, selectedOrgId])

  async function loadVoiceButtonsPage(page: number) {
    if (!selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED) {
      setVoiceButtons([])
      setVoiceButtonsMeta({ currentPage: 1 })
      return
    }

    setLoadingVoiceButtons(true)
    try {
      const response = await getRequestV5({
        endpoint: '/alert_sources',
        headers: { org_id: selectedOrgId },
        params: {
          filterBy: { field: 'deviceType', value: 'voice_button' },
          pagination: { currentPage: page },
        },
      })

      const data: ApiResponseV5 = response.data
      setVoiceButtons(Array.isArray(data?.data) ? data.data : [])
      setVoiceButtonsMeta(data?.meta ?? { currentPage: page })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load voice_button alert_sources:', err)
      setVoiceButtons([])
      setVoiceButtonsMeta({ currentPage: page })
    } finally {
      setLoadingVoiceButtons(false)
    }
  }

  useEffect(() => {
    if (!pickerOpen) return
    void loadVoiceButtonsPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerOpen, selectedOrgId])

  const currentPage = useMemo(() => Number(voiceButtonsMeta?.currentPage || 1), [voiceButtonsMeta])
  const canPrev = useMemo(() => currentPage > 1, [currentPage])

  const canNext = useMemo(() => {
    if (voiceButtonsMeta?.after) return true
    if (Number.isFinite(voiceButtonsMeta?.totalPages)) return currentPage < Number(voiceButtonsMeta.totalPages || 1)
    if (Number.isFinite(voiceButtonsMeta?.totalCount))
      return currentPage * 50 < Number(voiceButtonsMeta.totalCount || 0)
    return (voiceButtons || []).length >= 50
  }, [voiceButtonsMeta, currentPage, voiceButtons])

  const pageLabel = useMemo(() => {
    if (Number.isFinite(voiceButtonsMeta?.totalPages))
      return `Page ${currentPage} / ${Number(voiceButtonsMeta.totalPages)}`
    return `Page ${currentPage}`
  }, [voiceButtonsMeta, currentPage])

  const handlePrev = () => {
    if (currentPage <= 1) return
    void loadVoiceButtonsPage(currentPage - 1)
  }

  const handleNext = () => {
    void loadVoiceButtonsPage(currentPage + 1)
  }

  const groupDevices: Device[] = useMemo(() => {
    const list = Array.isArray((dg as any)?.alertSources) ? (dg as any).alertSources : []
    return uniqById(
      list
        .map((src: any) => {
          const id = normalizeDeviceId(src)
          if (!id) return null
          return {
            id,
            name: normalizeDeviceName(src),
            assignedTo: thisGroupLabel || null,
            assignedGroupId: String((dg as any)?.id ?? ''),
            raw: src,
          } as Device
        })
        .filter(Boolean) as Device[],
    ).sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }))
  }, [dg, thisGroupLabel])

  const alreadyInGroupIds = useMemo(() => groupDevices.map(d => d.id), [groupDevices])

  const pool: Device[] = useMemo(() => {
    if (!selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED) return []
    const all: Device[] = []
    for (const vb of voiceButtons || []) {
      const id = normalizeDeviceId(vb)
      if (!id) continue
      const assigned = assignmentByDeviceId.get(id)
      all.push({
        id,
        name: normalizeDeviceName(vb),
        assignedTo: assigned?.label || null,
        assignedGroupId: assigned?.groupId || null,
        raw: vb,
      })
    }
    return uniqById(all)
  }, [voiceButtons, assignmentByDeviceId, selectedOrgId])

  async function handleAddSelected(devices: Device[]) {
    if (!group || !dg) return
    if (!selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED) {
      alert('Please select an organization first.')
      return
    }
    if (!devices.length) return

    const nextGroupId = String((dg as any)?.id || '')
    if (!nextGroupId) return

    setSaving(true)
    try {
      for (let i = 0; i < devices.length; i++) {
        const d = devices[i]

        // A device should live in exactly one group. Remove it from every group
        // it currently belongs to (a device can be in several after earlier
        // moves), then add it to the target. Relying on a single "current group"
        // left devices behind in their old language group.
        const currentGroupIds = (deviceGroups || [])
          .filter(g => String((g as any)?.orgId || '') === String(selectedOrgId))
          .filter(g =>
            (Array.isArray((g as any)?.alertSources) ? (g as any).alertSources : []).some(
              (s: any) => normalizeDeviceId(s) === d.id,
            ),
          )
          .map(g => String((g as any)?.id || ''))
          .filter(gid => gid && gid !== nextGroupId)

        for (const gid of currentGroupIds) {
          await removeAlertSourceFromDeviceGroup(gid, d.id, selectedOrgId || '')
        }

        await addAlertSourceToDeviceGroup(nextGroupId, d.id, selectedOrgId || '')
      }

      await refreshDeviceGroups()
      await loadVoiceButtonsPage(currentPage)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to add devices:', e)
      alert('Failed to add devices. Check console.')
      await refreshDeviceGroups()
    } finally {
      setSaving(false)
    }
  }

  // ✅ NEW: remove a device from THIS device group (update device -> clear group)
  async function handleRemoveFromGroup(device: Device) {
    if (!group || !dg) return
    if (!selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED) {
      alert('Please select an organization first.')
      return
    }

    const currentGroupId = String((dg as any)?.id || '')
    if (!currentGroupId) return

    // guard: only remove if currently in THIS group
    if (String(device.assignedGroupId || '') !== String(currentGroupId)) return

    setSaving(true)
    try {
      await setAlertSourceDeviceGroup({
        currentGroupId: currentGroupId || null,
        nextGroupId: null, // ✅ remove group assignment
        alertSourceId: device.id,
        orgId: selectedOrgId || '',
      })

      await refreshDeviceGroups()
      await loadVoiceButtonsPage(currentPage)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to remove device from group:', e)
      alert('Failed to remove device. Check console.')
      await refreshDeviceGroups()
    } finally {
      setSaving(false)
    }
  }

  const title = group ? `Devices — ${group.baseChannelName} (${languageLabel(group.language)})` : 'Devices'

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle style={{ fontFamily: 'Montserrat', fontWeight: 900 }}>{title}</DialogTitle>

        <DialogContent dividers>
          {group ? (
            <>
              <div style={{ marginBottom: 10, color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>
                <span style={{ fontWeight: 900 }}>{groupDevices.length}</span>
                {' devices currently in '}
                <span style={{ fontWeight: 900 }}>{thisGroupLabel || group.linkedChannelName}</span>
              </div>

              <div style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 10, overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow style={{ background: 'rgba(0,0,0,0.03)' }}>
                      <TableCell style={{ fontWeight: 900 }}>{'Name'}</TableCell>
                      <TableCell style={{ fontWeight: 900 }}>{'ID'}</TableCell>
                      <ConditionalRender condition={isAdmin(selectedOrgId)}>
                        <TableCell style={{ fontWeight: 900, textAlign: 'right', width: 120 }}>{''}</TableCell>
                      </ConditionalRender>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {groupDevices.map(d => (
                      <TableRow key={d.id} hover>
                        <TableCell style={{ fontWeight: 800 }}>{d.name}</TableCell>
                        <TableCell style={{ color: 'rgba(0,0,0,0.65)', fontFamily: 'monospace', fontSize: 12 }}>
                          {d.id}
                        </TableCell>
                        <ConditionalRender condition={isAdmin(selectedOrgId)}>
                          <TableCell style={{ textAlign: 'right' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={saving}
                              onClick={() => handleRemoveFromGroup(d)}
                              style={{
                                borderColor: saving ? 'rgba(0,0,0,0.25)' : BRAND_COLOR,
                                color: saving ? 'rgba(0,0,0,0.35)' : BRAND_COLOR,
                                fontWeight: 900,
                                textTransform: 'none',
                                borderRadius: 14,
                                height: 28,
                                paddingLeft: 10,
                                paddingRight: 10,
                              }}
                            >
                              {'Remove'}
                            </Button>
                          </TableCell>
                        </ConditionalRender>
                      </TableRow>
                    ))}

                    {groupDevices.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} style={{ padding: 16, color: 'rgba(0,0,0,0.55)' }}>
                          {'No devices in this group yet.'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <div style={{ padding: 8, color: 'rgba(0,0,0,0.55)' }}>{'No group selected.'}</div>
          )}
        </DialogContent>

        <DialogActions style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between' }}>
          <ConditionalRender condition={isAdmin(selectedOrgId)}>
            <Button
              variant="contained"
              disabled={!group || saving}
              onClick={() => setPickerOpen(true)}
              style={{
                backgroundColor: !group || saving ? undefined : BRAND_COLOR,
                color: !group || saving ? undefined : '#fff',
                fontWeight: 900,
                textTransform: 'none',
                borderRadius: 18,
                height: 34,
                paddingLeft: 16,
                paddingRight: 16,
              }}
            >
              {saving ? 'Saving…' : 'Add Devices'}
            </Button>
          </ConditionalRender>

          <Button
            onClick={onClose}
            variant="outlined"
            style={{
              borderColor: BRAND_COLOR,
              color: BRAND_COLOR,
              fontWeight: 900,
              textTransform: 'none',
              borderRadius: 18,
              height: 34,
              paddingLeft: 14,
              paddingRight: 14,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <DevicePickerModal
        open={pickerOpen}
        group={group ? { baseChannelName: group.baseChannelName, language: group.language } : null}
        pool={pool}
        alreadyInGroupIds={alreadyInGroupIds}
        loading={loadingVoiceButtons || saving}
        pageLabel={pageLabel}
        canPrev={canPrev}
        canNext={canNext}
        onPrev={handlePrev}
        onNext={handleNext}
        onClose={() => setPickerOpen(false)}
        onAddSelected={handleAddSelected}
      />
    </>
  )
}

export default memo(VoiceDevicesModal)
